export type AppLanguage = 'zh-TW' | 'en-US'

export const DEFAULT_LANGUAGE: AppLanguage = 'zh-TW'
const LANGUAGE_STORAGE_KEY = 'yzu_ui_language'

export const getInitialLanguage = (): AppLanguage => {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE
  const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
  return stored === 'en-US' ? 'en-US' : DEFAULT_LANGUAGE
}

export const saveLanguage = (language: AppLanguage) => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  }
}

const translations = {
  'zh-TW': {
    appTitle: '元智大學校園助手',
    chatTitle: '元智大學校園助手',
    chatDescription: '校園相關的事項都可以問我喔！',
    switchLanguage: 'English',
    switchLanguageTitle: '切換成英文',
    personalSidebar: '個人工作區側欄',
    openSidebar: '開啟個人側欄',
    closeSidebar: '關閉個人側欄',
    staffId: '工號',
    studentId: '學號',
    defaultUser: '元智使用者',
    logout: '登出',
    loggingOut: '登出中…',
    history: '歷史對話',
    newChat: '新開對話',
    loadingHistory: '載入歷史對話',
    historyUnavailable: '歷史對話目前無法載入。',
    historyNotEnabled: '尚未啟用歷史對話儲存。',
    noHistory: '目前沒有歷史對話。',
    placeholder: '請輸入您想詢問的問題…',
    quickPrompts: '快速提問',
    campusQa: '校園問答',
    courseQa: '選課問答',
    processing: '正在分析您的問題…',
    currentProcessing: '目前處理：',
    stopGenerating: '停止產生回答',
    uploadImage: '上傳圖片',
    microphone: '麥克風',
    askQuestion: '送出問題',
    citations: '佐證資料',
    intents: '意圖',
    reference: '筆參考資料',
    references: '筆參考資料',
    aiDisclaimer: 'AI 生成內容可能不正確',
    processingTime: '處理時間',
    close: '關閉',
    delete: '刪除',
    edit: '編輯',
    confirmDelete: '確定要刪除這個對話嗎？',
    deleteWarning: '此對話的歷史紀錄將永久刪除。',
    cancel: '取消',
    deleteError: '錯誤：無法刪除對話',
    authenticationNotConfigured: '尚未設定登入驗證',
    contactAdministrator: '請聯絡網站管理員。',
    chatHistoryNotEnabled: '歷史對話未啟用',
    tryAgain: '請再試一次。若問題持續，請聯絡網站管理員。',
    errorGenerating: '產生回答時發生錯誤。',
    saveError: '回答目前無法儲存。',
  },
  'en-US': {
    appTitle: 'YZU Campus Assistant',
    chatTitle: 'YZU Campus Assistant',
    chatDescription: 'Ask me anything about Yuan Ze University.',
    switchLanguage: '中文',
    switchLanguageTitle: 'Switch to Traditional Chinese',
    personalSidebar: 'Personal workspace sidebar',
    openSidebar: 'Open personal sidebar',
    closeSidebar: 'Close personal sidebar',
    staffId: 'Staff ID',
    studentId: 'Student ID',
    defaultUser: 'YZU User',
    logout: 'Log out',
    loggingOut: 'Logging out…',
    history: 'Chat history',
    newChat: 'New chat',
    loadingHistory: 'Loading chat history',
    historyUnavailable: 'Chat history is currently unavailable.',
    historyNotEnabled: 'Chat history storage is not enabled.',
    noHistory: 'No chat history.',
    placeholder: 'Ask a question…',
    quickPrompts: 'Quick questions',
    campusQa: 'Campus Q&A',
    courseQa: 'Course Q&A',
    processing: 'Analyzing your question…',
    currentProcessing: 'Processing: ',
    stopGenerating: 'Stop generating',
    uploadImage: 'Upload image',
    microphone: 'Microphone',
    askQuestion: 'Ask question',
    citations: 'Sources',
    intents: 'Intents',
    reference: 'reference',
    references: 'references',
    aiDisclaimer: 'AI-generated content may be incorrect',
    processingTime: 'Processing time',
    close: 'Close',
    delete: 'Delete',
    edit: 'Edit',
    confirmDelete: 'Delete this conversation?',
    deleteWarning: 'The history of this conversation will be permanently removed.',
    cancel: 'Cancel',
    deleteError: 'Error: unable to delete conversation',
    authenticationNotConfigured: 'Authentication is not configured',
    contactAdministrator: 'Please contact the site administrator.',
    chatHistoryNotEnabled: 'Chat history is not enabled',
    tryAgain: 'Please try again. If the problem persists, contact the site administrator.',
    errorGenerating: 'An error occurred while generating a response.',
    saveError: 'The answer cannot be saved at this time.',
  }
} as const

export type TranslationKey = keyof typeof translations['zh-TW']

export const t = (language: AppLanguage, key: TranslationKey): string => translations[language][key]

export const localizedQuickPromptGroups = (language: AppLanguage) => language === 'en-US'
  ? [
      {
        title: t(language, 'campusQa'),
        prompts: [
          'Who is the current president?',
          'What orientation activities are available for new students?',
          'What should new students know before registration?',
          'When are tuition and registration fees due?',
          'How do I apply for on-campus housing?'
        ]
      },
      {
        title: t(language, 'courseQa'),
        prompts: [
          'What courses are available in Information Management?',
          'When does course registration start for new students?',
          'How do new students register for courses?',
          'What are the minimum and maximum course credits?',
          'Can TOEIC scores be used for credit transfer?'
        ]
      }
    ]
  : [
      {
        title: t(language, 'campusQa'),
        prompts: ['校長是誰？', '新生活動有哪些？', '新生報到要注意哪些事情？', '學費或註冊費什麼時候繳？', '宿舍如何申請？新生住宿要注意什麼？']
      },
      {
        title: t(language, 'courseQa'),
        prompts: ['資管系有哪些課程？', '新生什麼時候開始選課？', '新生要如何選課？', '選課上下限是多少？', '多益成績可以申請學分抵免嗎？']
      }
    ]
